import { loadReport } from '@quickreport/workspace';
import { isDesktop } from '../../lib/desktop';
import { loadProfile, pagesHtml, pdfToImages, printPdfs } from '../../lib/desktop-print';
import type { LocalDb } from '../../local-db/client';
import { renderReportPdf } from './report-pdf';
import { printHtmlInFrame } from './slip';

/** A report made into a PDF, with the name to give the file. */
export interface MadePdf {
  id: string;
  fileName: string;
  pdf: Uint8Array;
}

/** Only a report that has been signed and is not being corrected is handed out from a list. */
export const PRINTABLE = new Set(['final', 'partial']);

/** Make each report into its PDF, exactly as it prints from the report's own PDF tab. */
export async function makePdfs(
  db: LocalDb,
  orgId: string,
  orgName: string,
  verifyBaseUrl: string,
  ids: string[],
  onProgress?: (done: number, total: number) => void,
): Promise<MadePdf[]> {
  const out: MadePdf[] = [];
  for (const [i, id] of ids.entries()) {
    onProgress?.(i, ids.length);
    const r = await loadReport(db, id);
    if (!r) continue;
    const made = await renderReportPdf(db, orgId, orgName, verifyBaseUrl, r);
    out.push({
      id,
      fileName: `${r.labNo}-${r.content.patient.name.replace(/[^\w]+/g, '-')}.pdf`,
      pdf: made.pdf,
    });
  }
  onProgress?.(ids.length, ids.length);
  return out;
}

/** Save each PDF as a file, one after the other (a browser may ask once to allow several). */
export async function downloadPdfs(files: MadePdf[]): Promise<void> {
  for (const f of files) {
    const url = URL.createObjectURL(new Blob([f.pdf as BlobPart], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = f.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    await new Promise((r) => setTimeout(r, 450));
  }
}

/**
 * Print all of them as one print job. The desktop app uses its printer settings; a browser prints
 * page pictures from an off-screen frame, and if it will not print from there the files are
 * downloaded instead so nothing is lost.
 */
export async function printMany(files: MadePdf[]): Promise<{ ok: boolean; error?: string }> {
  if (files.length === 0) return { ok: true };
  if (isDesktop())
    return printPdfs(
      files.map((f) => f.pdf),
      loadProfile(),
    );
  const images: string[] = [];
  for (const f of files) images.push(...(await pdfToImages(f.pdf)));
  const html = pagesHtml(images, { paper: 'A4', offsetXmm: 0, offsetYmm: 0 });
  await printHtmlInFrame(html, () => void downloadPdfs(files));
  return { ok: true };
}
